import IInbound from "@/store/models/IInbound";
import IOutbound from "@/store/models/IOutbound";

export class TimeSlotCalculator {
    // eslint-disable-next-line no-useless-constructor
    constructor(
        private readonly getInboundsByDate: (time: string, idCompany: string) => Promise<IInbound[]>,
        private readonly getOutboundsByDate: (time: string, idCompany: string) => Promise<IOutbound[]>,
        private readonly getInboundsByDateOtherCompanies: (time: string, idCompany: string) => Promise<IInbound[]>,
        private readonly getOutboundsByDateOtherCompanies: (time: string, idCompany: string) => Promise<IOutbound[]>
    ) {}

    async getAvailableTimeSlots(
        time: string,
        idCompanyDestination: string,
        currentCompanySourceId: string,
        currentCompanyDestinationId: string,
        selectedInboundStartTime?: number,
        isDateChanged: boolean = false
    ): Promise<number[]> {
        const hoursToCheck: number[] = [7, 8, 9, 10, 11, 13, 14, 15, 17, 18, 19, 20, 21];
        if (!idCompanyDestination) return [];
        const isCurrentSameCompany =
            !!currentCompanySourceId &&
            !!currentCompanyDestinationId &&
            currentCompanySourceId === currentCompanyDestinationId;

        // ✅ Caso interno (STR→STR, etc.) => sin límite
        if (isCurrentSameCompany) {
            const res = hoursToCheck.slice();

            // Mantener el slot actual visible si NO cambiaste fecha (caso edición)
            if (!isDateChanged && selectedInboundStartTime !== undefined && selectedInboundStartTime !== 0) {
                if (res.indexOf(selectedInboundStartTime) === -1) res.push(selectedInboundStartTime);
            }

            return res.sort((a, b) => a - b);
        }

        // Helpers: normalizar start/end y contar por slot de 1 hora
        const toHour = (value: any): number | null => {
            if (value === null || value === undefined) return null;

            if (typeof value === "number" && Number.isFinite(value)) return value;

            if (typeof value === "string") {
                const v = value.trim();

                // "07:00" / "7:00" / "07"
                const m = v.match(/^(\d{1,2})(?::(\d{2}))?$/);
                if (m) {
                    const hh = Number(m[1]);
                    const mm = m[2] ? Number(m[2]) : 0;
                    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
                    return hh + mm / 60;
                }

                // "7.5" o "7,5"
                const asNum = Number(v.replace(",", "."));
                return Number.isFinite(asNum) ? asNum : null;
            }

            return null;
        };

        const overlaps = (slotStart: number, slotEnd: number, itemStart: number, itemEnd: number): boolean => {
            // Intervalos [slotStart, slotEnd) y [itemStart, itemEnd)
            return itemStart < slotEnd && itemEnd > slotStart;
        };

        // ✅ Caso cross-company => aplicar límite contando TODOS los items (in+out combinados)
        const [
            inbounds,
            outbounds,
            inboundsOther,
            outboundsOther
        ] = await Promise.all([
            this.getInboundsByDate(time, idCompanyDestination),
            this.getOutboundsByDate(time, idCompanyDestination),
            this.getInboundsByDateOtherCompanies(time, idCompanyDestination),
            this.getOutboundsByDateOtherCompanies(time, idCompanyDestination)
        ]);

        const allItems: any[] = ([] as any[]).concat(
            inbounds,
            outbounds,
            inboundsOther,
            outboundsOther
        );

        const slotLimit = 4;
        const slotCounts: Record<number, number> = {};

        for (const item of allItems) {
            const s = toHour(item.startTime);
            const e = toHour(item.endTime);

            if (s === null || e === null) continue;

            const itemStart = s;
            // seguridad por si end viene igual/menor
            const itemEnd = e > s ? e : s + 1;

            // Cuenta 1 por cada slot de 1h que solape con el item
            for (const hour of hoursToCheck) {
                const slotStart = hour;
                const slotEnd = hour + 1;

                if (overlaps(slotStart, slotEnd, itemStart, itemEnd)) {
                    slotCounts[hour] = (slotCounts[hour] || 0) + 1;
                }
            }
        }

        const available: number[] = [];
        for (const hour of hoursToCheck) {
            if ((slotCounts[hour] || 0) < slotLimit) {
                available.push(hour);
            }
        }

        // Mantener el slot actual visible si NO cambiaste fecha (caso edición)
        if (!isDateChanged && selectedInboundStartTime !== undefined && selectedInboundStartTime !== 0) {
            if (available.indexOf(selectedInboundStartTime) === -1) {
                available.push(selectedInboundStartTime);
            }
        }
        return available.sort((a, b) => a - b);
    }
}
